import { TFunction } from 'i18next'
import { leafOutline, logoEuro, starOutline, trophyOutline } from 'ionicons/icons'
import { ScoreboardCardStyle } from '../../components/scoreboard/scoreboardCard/ScoreboardCard'
import { TimePeriod, timePeriods } from '../../constants/scoreboardConstants'
import { AchievementsHook } from '../../hooks/useAchievements'
import { BadgeHook } from '../../hooks/useBadges'
import { ScoreboardHook } from '../../hooks/useScoreboard'
import { Level } from '../../types/level'
import { getLevelStyles } from './getLevelSyles'

interface ScoreboardCard {
	icon?: string
	title: string | number
	subtext: string
	iconStyle?: string
	star?: boolean
	style?: ScoreboardCardStyle
}

const formatCurrency = (amount: number): string => `${amount.toFixed(2).replace('.', ',')}€`

const formatPoints = (points: number): string => points.toLocaleString('fi-FI')

export const getScoreboardCardsByPeriod = (
	period: TimePeriod,
	scoreboardData: ScoreboardHook,
	achievementsData: AchievementsHook,
	badgeHook: BadgeHook,
	t: TFunction<'pages_saving', undefined>
): ScoreboardCard[] => {
	if (period === timePeriods.last) {
		return getLastPeriodCards(scoreboardData, achievementsData, t)
	} else {
		return getAllTimeCards(scoreboardData, achievementsData, badgeHook, t)
	}
}

const getLastPeriodCards = (
	scoreboardData: ScoreboardHook,
	achievementsData: AchievementsHook,
	t: TFunction<'pages_saving', undefined>
): ScoreboardCard[] => {
	const level = getLevelStyles(scoreboardData.lastLevel as Level, t)

	return [
		{
			icon: trophyOutline,
			title: level.title,
			subtext: t('getScoreboardCards.level'),
			iconStyle: level.iconColor,
			style: level,
		},
		{
			icon: leafOutline,
			title: `${t('getScoreboardCards.top')} ${scoreboardData.lastPercentage}%`,
			subtext: t('getScoreboardCards.percentage'),
			iconStyle: 'text-brand-secondary-100-light dark:text-brand-primary-100-dark',
		},
		{
			icon: starOutline,
			star: true,
			title: formatPoints(scoreboardData.lastPoints || 0),
			subtext: t('getScoreboardCards.pointsShort'),
		},
		{
			icon: logoEuro,
			title: formatCurrency(scoreboardData.lastSavings),
			subtext: t('getScoreboardCards.saved'),
			iconStyle: 'text-brand-secondary-100-light dark:text-brand-primary-100-dark',
		},
	]
}

const getAllTimeCards = (
	scoreboardData: ScoreboardHook,
	achievementsData: AchievementsHook,
	badgeHook: BadgeHook,
	t: TFunction<'pages_saving', undefined>
): ScoreboardCard[] => {
	const completedBadges = badgeHook.userBadges?.reduce((acc, ub) => acc + ub.earnedCount, 0)

	return [
		{
			icon: trophyOutline,
			iconStyle: 'text-[#ffb30d]',
			title: completedBadges || 0,
			subtext: t('getScoreboardCards.earnedBadges'),
		},
		{
			icon: leafOutline,
			title: achievementsData.achievements.filter(a => a.isCompleted).length,
			subtext: t('getScoreboardCards.goals'),
			iconStyle: 'text-brand-secondary-100-light dark:text-brand-primary-100-dark',
		},
		{
			star: true,
			title: formatPoints(scoreboardData.totalPoints),
			subtext: t('getScoreboardCards.pointsShort'),
		},
		{
			icon: logoEuro,
			title: formatCurrency(scoreboardData.totalSavings),
			subtext: t('getScoreboardCards.saved'),
			iconStyle: 'text-brand-secondary-100-light dark:text-brand-primary-100-dark',
		},
	]
}
